import React, { useContext, useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Home, Flame, User, Sun, Moon, LogOut, LogIn, Bell } from 'lucide-react';
import { cn, formatTime } from '../utils';
import { useTheme } from '../context/ThemeContext';
import { AppContext } from '../context/AppContext';

export default function Navbar() {
  const { theme, toggleTheme } = useTheme();
  const { token, logout, notifications, markNotificationsRead } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const mobileNotifRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (notifRef.current && notifRef.current.contains(event.target)) ||
        (mobileNotifRef.current && mobileNotifRef.current.contains(event.target))
      ) {
        return;
      }
      setShowNotifications(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleNotifications = () => {
    if (!showNotifications) {
      markNotificationsRead();
    }
    setShowNotifications(!showNotifications);
  };

  const unreadCount = notifications ? notifications.filter(n => !n.is_read).length : 0;

  const navItems = [
    { name: 'Feed', path: '/', icon: Home, show: true },
    { name: 'Trending', path: '/trending', icon: Flame, show: true },
    { name: 'Profile', path: '/profile', icon: User, show: !!token },
  ];

  return (
    <>
    <nav className="fixed top-0 inset-x-0 z-40 bg-white/90 dark:bg-selffo-dark/90 backdrop-blur-md border-b border-slate-100 dark:border-slate-900/50 transition-colors">
      <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">

        {/* Brand */}
        <NavLink to="/" className="flex items-center gap-2.5 group">
          <img 
            src={theme === 'dark' ? "/logo.png" : "/logo-light.png"} 
            alt="Selffo Ghost Logo" 
            className="w-8 h-8 rounded-lg shadow-sm transition-all group-hover:scale-110"
          />
          <span 
            className="text-2xl sm:text-3xl font-normal text-slate-900 dark:text-white tracking-wide"
            style={{ fontFamily: "'Unica One', sans-serif", paddingTop: "2px" }}
          >
            Selffo
          </span>
        </NavLink>

        {/* Top Navbar Actions - Hidden on Mobile */}
        <div className="hidden sm:flex items-center gap-2">
          {navItems.filter(i => i.show).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => cn(
                  "flex items-center gap-2 px-2 py-1.5 rounded-lg text-[15px] font-semibold transition-all group",
                  isActive
                    ? "text-slate-900 dark:text-white"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                )}
              >
                <Icon className={cn("w-5 h-5", "group-active:scale-95 transition-transform")} />
                <span className="hidden sm:block">{item.name}</span>
              </NavLink>
            );
          })}

          <div className="w-px h-6 bg-slate-200 dark:bg-slate-800 mx-1 sm:mx-2" />

          {token ? (
            <div className="relative flex items-center" ref={notifRef}>
              <button
                onClick={toggleNotifications}
                className="p-2 relative flex items-center text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 border-2 border-white dark:border-selffo-dark rounded-full"></span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-900/50">
                    <h3 className="font-bold text-slate-900 dark:text-white">Notifications</h3>
                  </div>
                  <div className="max-h-[350px] overflow-y-auto">
                    {notifications && notifications.length > 0 ? (
                      notifications.map(notif => (
                        <div key={notif.id} className={cn("px-4 py-3 border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer", !notif.is_read ? "bg-indigo-50/50 dark:bg-indigo-900/10" : "")} onClick={() => setShowNotifications(false)}>
                          <p className="text-[14px] text-slate-800 dark:text-slate-200 leading-snug">
                            {notif.message}
                          </p>
                          <span className="text-xs text-slate-400 dark:text-slate-500 mt-1 block">
                            {formatTime(notif.timestamp)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                        <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="p-2 ml-1 flex items-center text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all font-medium"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="p-2 px-3 flex items-center gap-2 text-indigo-500 hover:bg-indigo-500/10 rounded-xl transition-all font-medium"
            >
              <LogIn className="w-5 h-5" />
              <span className="hidden sm:block">Login</span>
            </button>
          )}

          <button
            onClick={toggleTheme}
            className="p-2 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-all"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </nav>

    {/* Mobile Bottom Footer Navigation */}
    <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 dark:bg-selffo-dark/95 backdrop-blur-lg border-t border-slate-100 dark:border-slate-900/50 pb-safe">
      <div className="max-w-3xl mx-auto flex items-center justify-around h-16 px-2">
        {navItems.filter(i => i.show).map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => cn(
                "flex flex-col items-center justify-center w-12 h-12 rounded-xl transition-all",
                isActive
                  ? "text-slate-900 dark:text-white"
                  : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              )}
            >
              <Icon className={cn("w-[22px] h-[22px]", "active:scale-90 transition-transform")} />
            </NavLink>
          );
        })}

        {token ? (
          <div className="relative" ref={mobileNotifRef}>
            <button
              onClick={toggleNotifications}
              className={cn(
                "relative flex flex-col items-center justify-center w-12 h-12 rounded-xl transition-all",
                showNotifications ? "text-slate-900 dark:text-white" : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
              )}
            >
              <Bell className={cn("w-[22px] h-[22px]", "active:scale-90 transition-transform")} />
              {unreadCount > 0 && (
                <span className="absolute top-3 right-3 w-2 h-2 bg-red-500 border-2 border-white dark:border-selffo-dark rounded-full"></span>
              )}
            </button>

            {showNotifications && (
              <div className="fixed left-4 right-4 bottom-20 sm:absolute sm:left-auto sm:right-0 sm:bottom-16 sm:w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl z-50 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200">
                <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800/60 bg-slate-50 dark:bg-slate-900/50">
                  <h3 className="font-bold text-slate-900 dark:text-white">Notifications</h3>
                </div>
                <div className="max-h-[350px] overflow-y-auto">
                  {notifications && notifications.length > 0 ? (
                    notifications.map(notif => (
                      <div key={notif.id} className={cn("px-4 py-3 border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer", !notif.is_read ? "bg-indigo-50/50 dark:bg-indigo-900/10" : "")} onClick={() => setShowNotifications(false)}>
                        <p className="text-[14px] text-slate-800 dark:text-slate-200 leading-snug">
                          {notif.message}
                        </p>
                        <span className="text-xs text-slate-400 dark:text-slate-500 mt-1 block">
                          {formatTime(notif.timestamp)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                      <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate('/login')}
            className="flex flex-col items-center justify-center w-12 h-12 rounded-xl text-indigo-500 transition-all"
          >
            <LogIn className="w-[22px] h-[22px] active:scale-90 transition-transform" />
          </button>
        )}

        {token && (
          <button
            onClick={handleLogout}
            className="flex flex-col items-center justify-center w-12 h-12 rounded-xl text-slate-500 hover:text-red-500 transition-all"
          >
            <LogOut className="w-[22px] h-[22px] active:scale-90 transition-transform" />
          </button>
        )}

        <button
          onClick={toggleTheme}
          className="flex flex-col items-center justify-center w-12 h-12 rounded-xl text-slate-500 hover:text-slate-900 dark:hover:text-white transition-all"
        >
          {theme === 'dark' ? <Moon className="w-[22px] h-[22px] active:scale-90 transition-transform" /> : <Sun className="w-[22px] h-[22px] active:scale-90 transition-transform" />}
        </button>
      </div>
    </div>
    </>
  );
}
